import { getEndpoints, getConfig, initConfig } from './config.js';
import { onAuthChange, logout, signup, login, authenticateWithToken, getAuthToken, getUser, isAuthenticated, getAuthState, checkUrlForToken, initAuth, setSupabaseSession } from './auth.js';
import { put, post, get, callWithAuth, fetchWithAuth } from './api.js';
import { checkEntitlement } from './entitlement.js';
import { createPaywallUrl, onPaywallEvent, getCurrentPaywall, isPaywallOpen, closePaywall, showPaywall } from './paywall.js';
import { trackEvent } from './tracking.js';
import { createCustomerPortal, redirectToCustomerPortal, initCustomerPortal } from './customerPortal.js';

/**
 * Lightswitch SDK Core
 * 
 * Main entry point for the vanilla JavaScript SDK.
 * Provides all core functionality without framework dependencies.
 */


// SDK state
let isInitialized = false;

/**
 * Initialize the Lightswitch SDK
 * @param {Object} config - Configuration object
 * @param {string} config.publishableKey - The publishable key for API requests
 * @param {string} [config.appSlug='default'] - The app slug for endpoint generation
 * @param {boolean} [config.autoAuth=true] - Whether to automatically check for auth tokens
 * @param {Object} [config.supabaseClient] - Optional Supabase client instance
 * @returns {Promise<void>}
 */
const init = async _ref => {
  let {
    publishableKey,
    appSlug = 'default',
    autoAuth = true,
    supabaseClient = null
  } = _ref;
  try {
    // Initialize configuration
    initConfig({
      publishableKey,
      appSlug,
      supabaseClient
    });

    // Initialize authentication if enabled
    if (autoAuth) {
      // Check URL for token first
      const foundToken = await checkUrlForToken();

      // If no token in URL, check for stored token
      if (!foundToken) {
        await initAuth();
      }
    }

    // Initialize customer portal DOM binding
    initCustomerPortal();

    isInitialized = true;
    console.log('Lightswitch SDK initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Lightswitch SDK:', error);
    throw error;
  }
};

/**
 * Check if SDK is initialized
 * @returns {boolean} True if initialized
 */
const getInitialized = () => isInitialized;

/**
 * Ensure SDK is initialized
 * @throws {Error} If SDK is not initialized
 */
const ensureInitialized = () => {
  if (!isInitialized) {
    throw new Error('Lightswitch SDK not initialized. Call init() first.');
  }
};

/**
 * Get current SDK version
 * @returns {string} SDK version
 */
const getVersion = () => '1.0.0';

/**
 * Main Lightswitch SDK object
 */
const Lightswitch = {
  // Core initialization
  init,
  getInitialized,
  getVersion,
  // Configuration
  getConfig: () => {
    ensureInitialized();
    return getConfig();
  },
  getEndpoints: () => {
    ensureInitialized();
    return getEndpoints();
  },
  // Authentication
  getAuthState: () => {
    ensureInitialized();
    return getAuthState();
  },
  isAuthenticated: () => {
    ensureInitialized();
    return isAuthenticated();
  },
  getUser: () => {
    ensureInitialized();
    return getUser();
  },
  getAuthToken: () => {
    ensureInitialized();
    return getAuthToken();
  },
  authenticateWithToken: async token => {
    ensureInitialized();
    return authenticateWithToken(token);
  },
  login: redirectUrl => {
    ensureInitialized();
    return login(redirectUrl);
  },
  signup: redirectUrl => {
    ensureInitialized();
    return signup(redirectUrl);
  },
  logout: async () => {
    ensureInitialized();
    return logout();
  },
  onAuthChange: listener => {
    ensureInitialized();
    return onAuthChange(listener);
  },
  setSupabaseSession: async (accessToken, refreshToken, expiresAt) => {
    ensureInitialized();
    return setSupabaseSession(accessToken, refreshToken, expiresAt);
  },
  initAuth: async () => {
    ensureInitialized();
    return initAuth();
  },
  // API requests
  fetchWithAuth: async (url, options) => {
    ensureInitialized();
    return fetchWithAuth(url, options);
  },
  callWithAuth: async (callFunction, options) => {
    ensureInitialized();
    return callWithAuth(callFunction, options);
  },
  api: {
    get: async (url, options) => {
      ensureInitialized();
      return get(url, options);
    },
    post: async (url, data, options) => {
      ensureInitialized();
      return post(url, data, options);
    },
    put: async (url, data, options) => {
      ensureInitialized();
      return put(url, data, options);
    }
  },
  // Entitlements
  checkEntitlement: async (feature, options) => {
    ensureInitialized();
    return checkEntitlement(feature);
  },
  // Paywall
  showPaywall: options => {
    ensureInitialized();
    return showPaywall(options);
  },
  closePaywall: () => {
    ensureInitialized();
    return closePaywall();
  },
  isPaywallOpen: () => {
    ensureInitialized();
    return isPaywallOpen();
  },
  getCurrentPaywall: () => {
    ensureInitialized();
    return getCurrentPaywall();
  },
  onPaywallEvent: listener => {
    ensureInitialized();
    return onPaywallEvent(listener);
  },
  // Utility methods
  createPaywallUrl: (paywallConfigId, token) => {
    ensureInitialized();
    return createPaywallUrl(paywallConfigId, token);
  },
  // Tracking (anonymous - no auth required)
  trackEvent: async (eventType, params, options) => {
    ensureInitialized();
    return trackEvent(eventType, params, options);
  },
  // Customer Portal
  createCustomerPortal: async (returnUrl) => {
    ensureInitialized();
    return createCustomerPortal(returnUrl);
  },
  redirectToCustomerPortal: async (returnUrl) => {
    ensureInitialized();
    return redirectToCustomerPortal(returnUrl);
  }
};

export { authenticateWithToken, callWithAuth, checkEntitlement, closePaywall, createCustomerPortal, createPaywallUrl, Lightswitch as default, fetchWithAuth, get, getAuthState, getAuthToken, getConfig, getCurrentPaywall, getEndpoints, getInitialized, getUser, getVersion, init, initAuth, isAuthenticated, isPaywallOpen, login, logout, onAuthChange, onPaywallEvent, post, put, redirectToCustomerPortal, setSupabaseSession, showPaywall, signup, trackEvent };
//# sourceMappingURL=index.js.map
